import React, { useEffect, useMemo, useState } from 'react';
import './NotificationBanner.css';

export type BannerSeverity = 'info' | 'warning' | 'error';

export interface BannerConfig {
  id: string;
  enabled: boolean;
  // Note: config comes from JS (`environment.js`), so severity may be typed as a plain string.
  // We validate/coerce at runtime.
  severity: BannerSeverity | string;
  message: string;
  dismissible?: boolean;
}

interface NotificationBannerProps {
  bannerKey: string; // used for localStorage namespace
  config?: BannerConfig | null;
}

function storageKey(bannerKey: string, id: string) {
  return `ikmpt_banner_dismissed:${bannerKey}:${id}`;
}

const NotificationBanner: React.FC<NotificationBannerProps> = ({ bannerKey, config }) => {
  const enabled = !!config?.enabled;
  const id = config?.id || '';
  const dismissible = !!config?.dismissible;

  const key = useMemo(() => storageKey(bannerKey, id), [bannerKey, id]);
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed flag when banner id changes.
  useEffect(() => {
    setDismissed(false);
    if (!id) return;
    try {
      setDismissed(localStorage.getItem(key) === '1');
    } catch {
      // ignore storage errors
    }
  }, [id, key]);

  const visible = enabled && !!config?.message && !dismissed;
  if (!visible) return null;

  const rawSeverity = String(config?.severity || 'info').toLowerCase();
  const severity: BannerSeverity = (rawSeverity === 'warning' || rawSeverity === 'error' || rawSeverity === 'info')
    ? (rawSeverity as BannerSeverity)
    : 'info';

  const onClose = () => {
    setDismissed(true);
    if (!dismissible) return;
    try {
      localStorage.setItem(key, '1');
    } catch {
      // ignore
    }
  };

  return (
    <div className={`notification-banner notification-banner--${severity}`} role="status">
      <div className="notification-banner__inner">
        <div className="notification-banner__message">{config?.message}</div>
        {dismissible && (
          <button
            type="button"
            className="notification-banner__close"
            aria-label="Dismiss notification"
            onClick={onClose}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default NotificationBanner;
